import { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, Grid, Typography, Alert, 
  FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox
} from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DropZone from '../common/DropZone';
import { uploadSingleImage, uploadDocument } from '../../services/upload.service';

const archiveSchema = z.object({
  title: z.string().min(1, 'Укажите название'),
  type: z.enum(['DOCUMENT', 'PHOTO', 'VIDEO', 'AUDIO', 'NEWSPAPER', 'OTHER']),
  category: z.string().min(1, 'Укажите категорию'),
  year: z.string().optional(),
  description: z.string().optional(),
  fileUrl: z.string().min(1, 'Укажите URL файла'), // Uploaded via DropZone
  thumbnailUrl: z.string().optional(), // Uploaded via DropZone
  isPublic: z.boolean().default(true),
});

type ArchiveFormValues = z.infer<typeof archiveSchema>;

interface ArchiveFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ArchiveFormValues) => Promise<void>;
  initialData?: ArchiveFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function ArchiveForm({ open, onClose, onSubmit, initialData, loading, error }: ArchiveFormProps) {
  const [fileUploading, setFileUploading] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    reset,
    setValue,
    watch,
  } = useForm<ArchiveFormValues>({
    resolver: zodResolver(archiveSchema),
    defaultValues: { 
      title: '', 
      type: 'DOCUMENT', 
      category: '', 
      year: '', 
      description: '', 
      fileUrl: '', 
      thumbnailUrl: '', 
      isPublic: true 
    },
  });

  const fileUrl = watch('fileUrl');
  const thumbnailUrl = watch('thumbnailUrl');
  const typeValue = watch('type');

  useEffect(() => {
    if (initialData) reset(initialData);
    else reset({ 
      title: '', 
      type: 'DOCUMENT', 
      category: '', 
      year: '', 
      description: '', 
      fileUrl: '', 
      thumbnailUrl: '', 
      isPublic: true 
    });
  }, [initialData, open, reset]);

  // Handle file upload via DropZone
  const handleFileUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setFileUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        // Use appropriate upload based on type
        if (typeValue === 'PHOTO') {
          const url = await uploadSingleImage(file);
          results.push({ url });
        } else {
          const url = await uploadDocument(file);
          results.push({ url });
        }
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setFileUploading(false);
    }
  }, [typeValue]);

  const handleFileChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('fileUrl', url);
  }, [setValue]);

  // Handle thumbnail upload via DropZone
  const handleThumbnailUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setThumbnailUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setThumbnailUploading(false);
    }
  }, []);

  const handleThumbnailChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('thumbnailUrl', url);
  }, [setValue]);

  // Get appropriate DropZone config based on type
  const getDropZoneConfig = () => {
    switch (typeValue) {
      case 'PHOTO':
        return { type: 'image', maxSize: 10, helperText: 'JPG, PNG, WebP до 10MB' };
      case 'VIDEO':
        return { type: 'video', maxSize: 50, helperText: 'MP4, WebM до 50MB' };
      default:
        return { type: 'document', maxSize: 50, helperText: 'PDF, DOC, DOCX до 50MB' };
    }
  };

  const config = getDropZoneConfig();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Редактировать архив' : 'Добавить в архив'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Название *" 
                error={!!errors.title} 
                helperText={errors.title?.message} 
                {...register('title')} 
              />
            </Grid>
            
            {/* Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.type}>
                <InputLabel>Тип *</InputLabel>
                <Select label="Тип *" {...register('type')}>
                  <MenuItem value="DOCUMENT">Документ</MenuItem>
                  <MenuItem value="PHOTO">Фото</MenuItem>
                  <MenuItem value="VIDEO">Видео</MenuItem>
                  <MenuItem value="AUDIO">Аудио</MenuItem>
                  <MenuItem value="NEWSPAPER">Газета</MenuItem>
                  <MenuItem value="OTHER">Другое</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Category */}
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth 
                label="Категория *" 
                error={!!errors.category} 
                helperText={errors.category?.message} 
                {...register('category')} 
              />
            </Grid>
            
            {/* Year */}
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth 
                label="Год" 
                {...register('year')} 
              />
            </Grid>
            
            {/* File Upload — DropZone */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Файл
              </Typography>
              <DropZone
                onUpload={handleFileUpload}
                type={config.type as 'image' | 'video' | 'document'}
                maxFiles={1}
                maxSize={config.maxSize}
                multiple={false}
                value={fileUrl || ''}
                onChange={handleFileChange}
                label="Перетащите файл или нажмите для выбора"
                helperText={config.helperText}
              />
              {fileUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
              {/* Alternative: External URL */}
              <TextField
                fullWidth
                label="или укажите URL файла"
                error={!!errors.fileUrl}
                helperText={errors.fileUrl?.message || 'Можно загрузить файл или указать внешнюю ссылку'}
                {...register('fileUrl')}
                sx={{ mt: 2 }}
              />
            </Grid>
            
            {/* Thumbnail Upload — DropZone */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Превью (для отображения в списке)
              </Typography>
              <DropZone
                onUpload={handleThumbnailUpload}
                type="image"
                maxFiles={1}
                maxSize={10}
                multiple={false}
                value={thumbnailUrl || ''}
                onChange={handleThumbnailChange}
                label="Перетащите изображение или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB"
              />
              {thumbnailUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Описание" 
                multiline 
                rows={3} 
                {...register('description')} 
              />
            </Grid>
            
            {/* isPublic */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox {...register('isPublic')} defaultChecked />}
                label="Доступно публично"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button 
          onClick={handleSubmit(onSubmit)} 
          variant="contained" 
          disabled={loading || fileUploading || thumbnailUploading}
        >
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}